import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, ShieldCheck, Facebook, Instagram, ExternalLink, Languages } from "lucide-react";
import { Logo } from "./Logo";
import { useLang } from "../lang/LanguageContext";

const QUICK_LINKS = [
    { label: "Home", path: "/" },
    { label: "Meet Asia", path: "/about" },
    { label: "Our Program", path: "/program" },
    { label: "Health, Safety & Licensing", path: "/safety" },
    { label: "Enrollment & Tuition", path: "/enroll" },
    { label: "Enrollment Forms", path: "/forms" },
    { label: "Parent Corner & FAQ", path: "/parents" },
    { label: "Schedule a Tour", path: "/contact" },
];

export default function Footer() {
    const { t } = useLang();
    return (
        <footer className="bg-soil text-cream/90" data-testid="site-footer">
            <div className="max-w-6xl mx-auto px-5 sm:px-8 pt-14 pb-8">
                <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-4">
                    <div>
                        <Logo light />
                        <p className="mt-4 text-sm leading-relaxed text-cream/70 max-w-xs">
                            {t("A play-based group family daycare — where little seeds grow into confident, curious kids.")}
                        </p>
                        <div className="flex gap-3 mt-5" aria-label={t("Social media links")}>
                            {[
                                { icon: Facebook, label: "Facebook", id: "facebook" },
                                { icon: Instagram, label: "Instagram", id: "instagram" },
                                { icon: ExternalLink, label: "TikTok", id: "tiktok" },
                            ].map(({ icon: Icon, label, id }) => (
                                <a
                                    key={id}
                                    href="#"
                                    aria-label={`${label} (${t("link coming soon")})`}
                                    data-testid={`footer-social-${id}`}
                                    className="p-2.5 rounded-full bg-cream/10 hover:bg-sun hover:text-soil transition-colors"
                                    onClick={(e) => e.preventDefault()}
                                    title={`${label} — ${t("link coming soon")}`}
                                >
                                    <Icon size={17} />
                                </a>
                            ))}
                        </div>
                    </div>

                    <nav aria-label={t("Footer navigation")}>
                        <h3 className="font-display font-semibold text-sun text-lg mb-4">{t("Explore")}</h3>
                        <ul className="space-y-2.5 text-sm">
                            {QUICK_LINKS.map((l) => (
                                <li key={l.path}>
                                    <Link to={l.path} data-testid={`footer-link-${l.path.replace("/", "") || "home"}`} className="hover:text-sun transition-colors">
                                        {t(l.label)}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    <div>
                        <h3 className="font-display font-semibold text-sun text-lg mb-4">{t("Say Hello")}</h3>
                        <ul className="space-y-3 text-sm">
                            <li className="flex items-start gap-2.5">
                                <Phone size={16} className="text-sage mt-0.5 shrink-0" />
                                <a href="tel:+15163243418" data-testid="footer-phone-link" className="hover:text-sun transition-colors">
                                    {t("Call or text 516-324-3418")}
                                </a>
                            </li>
                            <li className="flex items-start gap-2.5" data-testid="footer-se-habla-espanol">
                                <Languages size={16} className="text-sage mt-0.5 shrink-0" />
                                <span>Se habla español</span>
                            </li>
                            <li className="flex items-start gap-2.5">
                                <Mail size={16} className="text-sage mt-0.5 shrink-0" />
                                <a href="mailto:littleseedsflourish@gmail.com" data-testid="footer-email-link" className="hover:text-sun transition-colors">
                                    littleseedsflourish@gmail.com
                                </a>
                            </li>
                            <li className="flex items-start gap-2.5">
                                <MapPin size={16} className="text-sage mt-0.5 shrink-0" />
                                <span>Massapequa Park, NY</span>
                            </li>
                            <li className="flex items-start gap-2.5">
                                <Clock size={16} className="text-sage mt-0.5 shrink-0" />
                                <span>
                                    {t("Mon–Fri 6:00 AM – 8:00 PM")}
                                    <br />
                                    {t("Sat 9:00 AM – 6:00 PM")}
                                </span>
                            </li>
                        </ul>
                    </div>

                    <div>
                        <h3 className="font-display font-semibold text-sun text-lg mb-4">{t("Licensing")}</h3>
                        <ul className="space-y-3 text-sm">
                            <li className="flex items-start gap-2.5">
                                <ShieldCheck size={16} className="text-sage mt-0.5 shrink-0" />
                                <span>
                                    {t("OCFS license active")}
                                    <br />
                                    {t("Group Family Daycare")}
                                </span>
                            </li>
                            <li>
                                <a
                                    href="https://hs.ocfs.ny.gov/DCFS"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="footer-ocfs-search-link"
                                    className="inline-flex items-center gap-1.5 text-sage hover:text-sun transition-colors underline underline-offset-4"
                                >
                                    {t("OCFS Child Care Facility Search")} <ExternalLink size={13} />
                                </a>
                            </li>
                            <li>
                                <a
                                    href="https://ocfs.ny.gov/programs/childcare/regulations/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="footer-ocfs-regs-link"
                                    className="inline-flex items-center gap-1.5 text-sage hover:text-sun transition-colors underline underline-offset-4"
                                >
                                    {t("OCFS Regulations (Part 416)")} <ExternalLink size={13} />
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>

                <div className="border-t border-cream/15 mt-10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-cream/55">
                    <p>© {new Date().getFullYear()} Little Seeds Flourish · Massapequa Park, NY</p>
                    <p data-testid="footer-privacy-note">{t("We never post a child's full name or share your family's information.")}</p>
                </div>
            </div>
        </footer>
    );
}
